// client/src/pages/CampaignSummary.js
// Campaign summary page — wrapped in SidebarLayout, row-based listing style
// matching the dashboard Content Health / Overview aesthetic.
// UPDATED Jun 2026: Full rewrite — removed tile cards, replaced with flat
//                   bordered row sections. Wrapped in SidebarLayout. Matches
//                   dashboard typography: Work Sans, uppercase section labels,
//                   brand-colour metrics, hairline borders throughout.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import SidebarLayout from '../components/SidebarLayout';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://geojetpack-production.up.railway.app'}/api`;

const colors = {
  primary:   '#39393a',
  teal:      '#048ba8',
  coral:     '#e76f51',
  green:     '#01796f',
  amber:     '#f4a261',
  textMuted: '#6b7280',
  border:    '#e5e7eb',
  bgSubtle:  '#f9fafb',
};

// ── Shared primitives ─────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colors.textMuted,
    marginBottom: '12px'
  }}>
    {children}
  </div>
);

const Section = ({ children, style = {} }) => (
  <div style={{
    backgroundColor: 'white',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '20px',
    ...style
  }}>
    {children}
  </div>
);

const SectionHeader = ({ children }) => (
  <div style={{
    padding: '14px 20px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle
  }}>
    {children}
  </div>
);

const Row = ({ label, value, valueColor, last = false }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: last ? 'none' : `1px solid ${colors.border}`,
  }}>
    <span style={{ fontSize: '13px', color: colors.textMuted }}>{label}</span>
    <span style={{
      fontSize: '13px',
      fontWeight: '500',
      color: valueColor || colors.primary,
      textAlign: 'right',
      maxWidth: '60%',
      wordBreak: 'break-word'
    }}>
      {value || '—'}
    </span>
  </div>
);

const ListRow = ({ primary, secondary, last = false }) => (
  <div style={{
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '11px 20px',
    borderBottom: last ? 'none' : `1px solid ${colors.border}`,
  }}>
    <span style={{
      width: '4px',
      height: '4px',
      borderRadius: '50%',
      backgroundColor: colors.teal,
      flexShrink: 0,
      marginTop: '7px'
    }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: '13px',
        fontWeight: '500',
        color: colors.primary,
        wordBreak: 'break-word',
        lineHeight: '1.4'
      }}>
        {primary}
      </div>
      {secondary && (
        <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
          {secondary}
        </div>
      )}
    </div>
  </div>
);

const EmptyRow = ({ message }) => (
  <div style={{
    padding: '20px',
    fontSize: '13px',
    color: colors.textMuted,
    fontStyle: 'italic'
  }}>
    {message}
  </div>
);

const MetricPill = ({ value, label }) => (
  <div style={{ textAlign: 'left' }}>
    <div style={{
      fontSize: '28px',
      fontWeight: '700',
      color: colors.teal,
      lineHeight: 1,
      marginBottom: '4px'
    }}>
      {value}
    </div>
    <div style={{
      fontSize: '10px',
      fontWeight: '600',
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: colors.textMuted
    }}>
      {label}
    </div>
  </div>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const extractDomain = (url) => {
  if (!url) return null;
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace('www.', '');
  } catch {
    return url;
  }
};

const formatDate = (val) => {
  if (!val) return '—';
  try { return new Date(val).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

// ── Main component ────────────────────────────────────────────────────────────

function CampaignSummary({ campaigns: propCampaigns, apiBaseUrl: propApiBaseUrl }) {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [campaign,    setCampaign]    = useState(null);
  const [prompts,     setPrompts]     = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState(propCampaigns || []);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const apiBase = propApiBaseUrl || API_BASE_URL;

  useEffect(() => {
    if (!campaignId) return;
    loadData();
  }, [campaignId]);

  // Also fetch the campaigns list if not passed as props (needed for SidebarLayout)
  useEffect(() => {
    if (propCampaigns?.length) { setAllCampaigns(propCampaigns); return; }
    if (!user?.id) return;
    fetch(`${apiBase}/campaigns`, { headers: { 'x-user-id': user.id } })
      .then(r => r.ok ? r.json() : { campaigns: [] })
      .then(d => setAllCampaigns(d.campaigns || d || []))
      .catch(() => setAllCampaigns([]));
  }, [user?.id, propCampaigns]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [campRes, promptsRes, compRes] = await Promise.all([
        fetch(`${apiBase}/campaigns/${campaignId}`),
        fetch(`${apiBase}/campaigns/${campaignId}/prompts`),
        fetch(`${apiBase}/campaigns/${campaignId}/competitors`),
      ]);

      if (!campRes.ok) throw new Error('Failed to load campaign');
      setCampaign(await campRes.json());

      setPrompts(promptsRes.ok ? (await promptsRes.json()) : []);
      setCompetitors(compRes.ok ? (await compRes.json()) : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SidebarLayout
        currentCampaign={null}
        campaigns={allCampaigns}
        apiBaseUrl={apiBase}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          height: '100%', fontFamily: "'Work Sans', sans-serif"
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              border: `3px solid ${colors.border}`,
              borderTopColor: colors.teal,
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ fontSize: '14px', color: colors.textMuted }}>Loading campaign…</p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error || !campaign) {
    return (
      <SidebarLayout
        currentCampaign={null}
        campaigns={allCampaigns}
        apiBaseUrl={apiBase}
      >
        <div style={{ padding: '32px', fontFamily: "'Work Sans', sans-serif" }}>
          <div style={{
            border: `1px solid #fca5a5`, borderRadius: '8px',
            padding: '20px', backgroundColor: '#fef2f2'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
              Could not load campaign
            </div>
            <div style={{ fontSize: '13px', color: '#b91c1c' }}>{error}</div>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const brandDomain = extractDomain(campaign.urls?.[0]);
  const engines = [
    campaign.audit_chatgpt  !== false && 'ChatGPT',
    campaign.audit_claude   !== false && 'Claude',
    campaign.audit_gemini   !== false && 'Gemini',
    campaign.audit_perplexity !== false && 'Perplexity',
  ].filter(Boolean);

  // ── Page ───────────────────────────────────────────────────────────────────
  return (
    <SidebarLayout
      currentCampaign={campaign}
      campaigns={allCampaigns}
      apiBaseUrl={apiBase}
    >
      <div style={{
        padding: '28px 32px',
        fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        maxWidth: '960px'
      }}>

        {/* ── Top metrics bar ────────────────────────────────────────────── */}
        <SectionLabel>Campaign overview</SectionLabel>

        <div style={{
          display: 'flex',
          gap: '40px',
          flexWrap: 'wrap',
          marginBottom: '28px',
          paddingBottom: '24px',
          borderBottom: `1px solid ${colors.border}`
        }}>
          <MetricPill value={prompts.length}     label="Prompts" />
          <MetricPill value={campaign.urls?.length || 0} label="URLs" />
          <MetricPill value={competitors.length} label="Competitors" />
          <MetricPill
            value={engines.length > 0 ? engines.length : '—'}
            label="Engines"
          />
          <div style={{ marginLeft: 'auto', textAlign: 'right', alignSelf: 'flex-end' }}>
            <div style={{ fontSize: '11px', color: colors.textMuted, marginBottom: '2px' }}>Created</div>
            <div style={{ fontSize: '13px', fontWeight: '500', color: colors.primary }}>
              {formatDate(campaign.created_at)}
            </div>
          </div>
        </div>

        {/* ── Two-column grid ────────────────────────────────────────────── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: '20px',
          marginBottom: '20px'
        }}>

          {/* Brand details */}
          <Section>
            <SectionHeader>Brand details</SectionHeader>
            <Row label="Campaign name"  value={campaign.name} />
            <Row label="Brand name"     value={campaign.brand_name} />
            <Row label="Industry"       value={campaign.industry} />
            <Row label="Target market"  value={campaign.target_market} />
            <Row label="Page / campaign type" value={campaign.page_type || campaign.campaign_type} />
            <Row label="Brand domain"   value={brandDomain} last />
          </Section>

          {/* Audit settings */}
          <Section>
            <SectionHeader>Audit settings</SectionHeader>
            <Row
              label="Engines"
              value={engines.length > 0 ? engines.join(', ') : 'None configured'}
            />
            <Row
              label="Auto-audit"
              value={campaign.auto_audit_enabled ? 'Enabled' : 'Disabled'}
              valueColor={campaign.auto_audit_enabled ? colors.green : colors.textMuted}
            />
            <Row
              label="Frequency"
              value={campaign.audit_frequency_hours ? `Every ${campaign.audit_frequency_hours}h` : '—'}
            />
            <Row
              label="Last run"
              value={formatDate(campaign.last_llm_audit_run)}
              last
            />
          </Section>
        </div>

        {/* ── Prompts ────────────────────────────────────────────────────── */}
        <Section>
          <SectionHeader>
            Prompts · {prompts.length} configured
          </SectionHeader>
          {prompts.length === 0
            ? <EmptyRow message="No prompts configured for this campaign." />
            : prompts.map((p, i) => (
              <ListRow
                key={p.id || i}
                primary={p.promptText || p.prompt_text || p.text || p.prompt || 'Untitled prompt'}
                secondary={p.prompt_class ? `Class ${p.prompt_class}` : null}
                last={i === prompts.length - 1}
              />
            ))
          }
        </Section>

        {/* ── URLs ───────────────────────────────────────────────────────── */}
        <Section>
          <SectionHeader>
            Monitored URLs · {campaign.urls?.length || 0} configured
          </SectionHeader>
          {!campaign.urls || campaign.urls.length === 0
            ? <EmptyRow message="No URLs configured for this campaign." />
            : campaign.urls.map((url, i) => (
              <ListRow
                key={i}
                primary={url}
                secondary={`URL ${i + 1}`}
                last={i === campaign.urls.length - 1}
              />
            ))
          }
        </Section>

        {/* ── Competitors ────────────────────────────────────────────────── */}
        <Section>
          <SectionHeader>
            Competitors · {competitors.length} tracked
          </SectionHeader>
          {competitors.length === 0
            ? <EmptyRow message="No competitors configured for this campaign." />
            : competitors.map((c, i) => {
              const name = c.competitorName || c.competitor_name || c.name;
              const url  = c.competitorUrl  || c.competitor_url  || c.url;
              const displayName = name || extractDomain(url) || 'Unknown competitor';
              return (
                <ListRow
                  key={c.id || i}
                  primary={displayName}
                  secondary={url || null}
                  last={i === competitors.length - 1}
                />
              );
            })
          }
        </Section>

        {/* ── Quick actions ──────────────────────────────────────────────── */}
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          paddingTop: '4px'
        }}>
          {[
            { label: 'View prompts',    route: `/campaign/${campaignId}/prompts` },
            { label: 'Content health',  route: `/campaign/${campaignId}/urls` },
            { label: 'Offsite',         route: `/campaign/${campaignId}/site` },
            { label: 'Report builder',  route: `/campaign/${campaignId}/reports` },
          ].map(({ label, route }) => (
            <button
              key={route}
              onClick={() => navigate(route)}
              style={{
                padding: '9px 18px',
                fontSize: '13px',
                fontWeight: '500',
                color: colors.teal,
                backgroundColor: 'white',
                border: `1px solid ${colors.teal}`,
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                fontFamily: "'Work Sans', sans-serif"
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = colors.teal;
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = colors.teal;
              }}
            >
              {label}
            </button>
          ))}
        </div>

      </div>
    </SidebarLayout>
  );
}

export default CampaignSummary;
